export type SessionUser = {
  identity: string
  name: string
  email?: string
  isAdmin: boolean
}

export type VoiceItem = {
  publicId: string
  title: string
  body: string
  departmentCode?: string
  category: string
  workflowStatus: 'received' | 'in_progress' | 'resolved'
  publishedAt: string
  likeCount: number
  dislikeCount: number
  viewerVote: number
}

export type AdminVoiceItem = VoiceItem & {
  rawText: string
  riskLevel: 'low' | 'medium' | 'high' | 'critical'
  sensitivityLabels: string[]
  moderationState: string
  visibility: string
  reviewReason: string
  adminNote: string
  createdAt: string
}

export type VoiceStats = {
  published: number
  pending: number
  inProgress: number
  resolved: number
}

type SessionPayload = { accessToken: string; user: SessionUser }

const TOKEN_KEY = 'nta_voice_access_token'
const USER_KEY = 'nta_voice_user'
const AUTH_URL = 'https://accounts.gearvn.xyz'

export function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) ?? ''
}

export function getStoredUser(): SessionUser | null {
  try {
    const value = localStorage.getItem(USER_KEY)
    return value ? (JSON.parse(value) as SessionUser) : null
  } catch {
    return null
  }
}

function saveSession(payload: SessionPayload): SessionUser {
  localStorage.setItem(TOKEN_KEY, payload.accessToken)
  localStorage.setItem(USER_KEY, JSON.stringify(payload.user))
  return payload.user
}

function saveUser(user: SessionUser): SessionUser {
  localStorage.setItem(USER_KEY, JSON.stringify(user))
  return user
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
}

export function getSsoLoginUrl(): string {
  const redirectUri = `${window.location.origin}/auth/sso/callback`
  return `${AUTH_URL}/login?redirect_uri=${encodeURIComponent(redirectUri)}`
}

export async function exchangeSso(code: string): Promise<SessionUser> {
  const response = await fetch('/api/auth/callback', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ code }),
  })
  const body = await readJSON<SessionPayload & { error?: string }>(response)
  if (!response.ok) throw new Error(body.error || 'Không xác thực được SSO')
  return saveSession(body)
}

export async function refreshSession(): Promise<SessionUser | null> {
  const response = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' })
  if (!response.ok) {
    clearSession()
    return null
  }
  return saveSession(await readJSON<SessionPayload>(response))
}

export async function getCurrentUser(): Promise<SessionUser> {
  const payload = await request<{ user: SessionUser }>('/api/auth/me')
  return saveUser(payload.user)
}

export async function logout(): Promise<void> {
  await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => undefined)
  clearSession()
  const redirectUri = encodeURIComponent(window.location.origin)
  window.location.href = `${AUTH_URL}/logout?redirect_uri=${redirectUri}`
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
      ...(init?.headers ?? {}),
    },
  })
  if (response.status === 401) {
    clearSession()
    throw new Error('Phiên đăng nhập đã hết hạn')
  }
  const body = await readJSON<T & { error?: string; message?: string }>(response)
  if (!response.ok) throw new Error(body.message || body.error || `HTTP ${response.status}`)
  return body
}

export const loadFeed = () => request<{ items: VoiceItem[]; stats: VoiceStats }>('/api/feed')

export const vote = (publicId: string, value: 1 | -1) =>
  request<{ vote: number; likeCount: number; dislikeCount: number }>(`/api/submissions/${encodeURIComponent(publicId)}/vote`, {
    method: 'POST',
    body: JSON.stringify({ vote: value }),
  })

export const loadAdmin = (state = '') =>
  request<{ items: AdminVoiceItem[] }>(`/api/admin/submissions${state ? `?state=${encodeURIComponent(state)}` : ''}`)

export const updateAdmin = (publicId: string, body: Record<string, unknown>) =>
  request<Record<string, unknown>>(`/api/admin/submissions/${encodeURIComponent(publicId)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  })

async function readJSON<T>(response: Response): Promise<T> {
  const text = await response.text()
  if (!text) return {} as T
  try {
    return JSON.parse(text) as T
  } catch {
    throw new Error('Máy chủ trả về dữ liệu không hợp lệ')
  }
}
